import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { H1Title, Paragraph } from "../components/typography/typography";
import { AppButton } from "../components/buttons/button";
import AuthGeneralInput from "../components/input/authinput";
import SubmissionService from "../services/submissionService";
import CompetitionService from "../services/competitionService";
import { Success, Error as ToastError } from "../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo, faCheck, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { getEmbedUrl } from "../helpers/videoEmbed";

export default function VideoPitch() {
  const [submission, setSubmission] = useState(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadSubmission(); }, []);

  const loadSubmission = async () => {
    try {
      const { data: compData } = await CompetitionService.getActive();
      const { data } = await SubmissionService.getMySubmission(compData.data.competition._id);
      const sub = data.data.submission;
      setSubmission(sub);
      if (sub?.videoPitch?.url) setUrl(sub.videoPitch.url);
    } catch { /* no submission */ }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) { ToastError('Please enter a video URL'); return; }
    setSubmitting(true);
    try {
      await SubmissionService.submitVideoPitch(submission._id, { url });
      Success('Video pitch submitted!');
      loadSubmission();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="py-20 text-center text-grey-667">Loading...</div>;

  if (!submission || submission.status === 'draft') {
    return (
      <div className="max-w-lg mx-auto text-center py-20">
        <H1Title size="sm">Video Pitch</H1Title>
        <Paragraph className="text-grey-667 mt-2">You must submit your written proposal before uploading a video pitch.</Paragraph>
        <AppButton onClick={() => navigate('/submission/new')} fullWidth={false} className="mt-6 px-8">Go to Proposal</AppButton>
      </div>
    );
  }

  if (submission.currentRound !== 'round2') {
    return (
      <div className="max-w-lg mx-auto text-center py-20">
        <H1Title size="sm">Video Pitch</H1Title>
        <Paragraph className="text-grey-667 mt-2">
          {submission.videoPitch?.url
            ? 'Your video pitch has been submitted.'
            : 'Video pitch submissions open in Round 2.'}
        </Paragraph>
        {submission.videoPitch?.url && (
          <div className="mt-6">
            <a href={submission.videoPitch.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" /> View your pitch
            </a>
          </div>
        )}
      </div>
    );
  }

  const embedUrl = getEmbedUrl(url);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary-bg rounded-full flex items-center justify-center mx-auto mb-4">
          <FontAwesomeIcon icon={faVideo} className="text-primary text-2xl" />
        </div>
        <H1Title size="sm">Record & Submit Your Pitch</H1Title>
        <Paragraph size="base" className="text-grey-667 mt-2 max-w-md mx-auto">
          Record a video pitch (max 15 minutes) and paste the link below. Upload to YouTube, Vimeo, or Google Drive.
        </Paragraph>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthGeneralInput
          label="Video URL"
          type="url"
          name="videoUrl"
          placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
          value={url}
          change={(e) => setUrl(e.target.value)}
        />

        {/* Preview */}
        {embedUrl && (
          <div className="rounded-xl overflow-hidden border border-grey-200 bg-black aspect-video">
            <iframe src={embedUrl} className="w-full h-full" allowFullScreen allow="autoplay; encrypted-media" title="Video preview" />
          </div>
        )}
        {url && !embedUrl && (
          <div className="bg-grey-bg rounded-lg p-4 text-center">
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">
              <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" /> Open link in new tab
            </a>
            <p className="text-xs text-grey-98 mt-1">Preview not available for this URL type</p>
          </div>
        )}

        <AppButton type="submit" loading={submitting}>
          {submission.videoPitch?.url ? (
            <><FontAwesomeIcon icon={faCheck} className="mr-2" /> Update Pitch</>
          ) : (
            'Submit Video Pitch'
          )}
        </AppButton>
      </form>

      <div className="bg-grey-bg rounded-lg p-4 text-sm text-grey-667">
        <p className="font-semibold text-grey-dark mb-2">Tips:</p>
        <ul className="list-disc list-inside space-y-1 text-xs">
          <li>Maximum 15 minutes</li>
          <li>Ensure the link is publicly accessible or shared with the right permissions</li>
          <li>YouTube and Vimeo links will show an inline preview</li>
          <li>Google Drive: make sure to set sharing to "Anyone with the link"</li>
        </ul>
      </div>
    </div>
  );
}
